import React from 'react';
import { StatusContextValue, useContextStatus } from '../common/ContextStatus';
import { useOktaAuth } from '@okta/okta-react';
import {useDeviceContext} from "../TelematicsDevice/TelematicsDeviceProvider";
import {DeviceStateApi} from "../TelematicsDevice/DeviceStateApi";
import {useAccessibilityContext} from "../AccessibilityProvider/AccessibilityProvider";

type InputFilterContextValue = StatusContextValue & {
  enabled: boolean | undefined;
  setEnabled: (enabled: boolean | undefined) => void;
};

const InputFilterContext = React.createContext<
  InputFilterContextValue | undefined
>(undefined);

type InputFilterProviderProps = {
  children: React.ReactNode;
};

const InputFilterProvider = (props: InputFilterProviderProps) => {
  const [enabled, setEnabled] = React.useState<boolean | undefined>(undefined);

  const [contextStatus, setContextStatus] = React.useState({
    loading: false,
    saving: false,
    hasContent: false
  });

  const value: InputFilterContextValue = React.useMemo(
    () => ({
      enabled,
      setEnabled,
      contextStatus,
      setContextStatus
    }),
    [enabled, contextStatus]
  );
  return <InputFilterContext.Provider value={value} {...props} />;
};

const useInputFilterContext = () => {
  const context = React.useContext(InputFilterContext);
  const { authState } = useOktaAuth();

  if (!context) {
    throw new Error(
      'useInputFilterContext must be used within an InputFilterProvider'
    );
  }

  const status = useContextStatus(context);
  const { enabled, setEnabled, contextStatus } = context;
  const { identity} = useDeviceContext();
  const {accessibility} = useAccessibilityContext();

  const save = (newEnabled: boolean) => {
    status.savingStarted();

    if ( !accessibility?.inputFiltering.visible || newEnabled === undefined) {
      return Promise.reject('User not allowed to toggle the input filtering');
    }

    return DeviceStateApi.updateState(authState?.accessToken, identity.id,
      {
        io: {
          inputFiltering: {
            enabled: newEnabled ? 'true' : 'false'
          }
        }
      })
      .then(() => {
        setEnabled(newEnabled);
        status.succeed();
      })
      .catch(err => {
        status.failed(err.message, 'Could not update input filtering');
      });
  };

  return {
    enabled,

    save,

    contextStatus,
    dismissError: status.dismissError,
    dismissSuccess: status.dismissSuccess
  };
};

export { InputFilterProvider, useInputFilterContext };
